import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, RefreshCw } from 'lucide-react';
import { verticalsApi, ApiError } from '../../lib/api';
import type { VerticalsSummaryResponse } from '../../types/verticals';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { isTradeVerticalId, TRADE_VERTICAL_LABELS, type TradeVerticalId } from './verticalIds';
import VerticalControlsPanel from './VerticalControlsPanel';
import VerticalGrowthStrip from './VerticalGrowthStrip';

function boolLabel(v: unknown): string {
  if (v === true) return 'On';
  if (v === false) return 'Off';
  return '—';
}

/**
 * Sprint 1–2 — vertical posture, PnL, growth strip, and editable controls.
 */
export default function VerticalOverviewTab() {
  const { verticalId } = useParams<{ verticalId: string }>();
  const vid = (verticalId || '').toLowerCase();
  const ok = isTradeVerticalId(vid);

  const [summary, setSummary] = useState<VerticalsSummaryResponse | null>(null);
  const [pnlPayload, setPnlPayload] = useState<Record<string, unknown> | null>(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!ok) return;
    setLoading(true);
    setErr('');
    try {
      const [s, p] = await Promise.all([verticalsApi.summary(), verticalsApi.pnl()]);
      setSummary(s);
      setPnlPayload(p as any);
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [ok]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!ok) return null;

  const slice = summary?.verticals?.[vid];
  const cfg = (slice?.config ?? {}) as Record<string, unknown>;
  const goals = (slice?.goals ?? {}) as Record<string, unknown>;
  const pnlV = (pnlPayload?.verticals as Record<string, any> | undefined)?.[vid];
  const label = TRADE_VERTICAL_LABELS[vid as TradeVerticalId] || vid;
  const vertical = vid as string;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      {err ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{err}</div>
      ) : null}

      {loading && !summary ? (
        <div className="flex justify-center py-12 text-muted-foreground">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : null}

      {slice && (
        <>
          <VerticalGrowthStrip verticalId={vertical} goals={goals} pnl={pnlV} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Posture — {label}</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-3">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">
                    Patterns {slice.patterns.active}/{slice.patterns.total} active
                  </Badge>
                  {pnlV ? (
                    <Badge variant="secondary">Open positions: {Number(pnlV.open_count ?? 0)}</Badge>
                  ) : null}
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-muted-foreground">
                  <div>
                    <dt className="text-xs uppercase tracking-wide">Scan</dt>
                    <dd className="text-foreground font-medium">{boolLabel(cfg.scan_enabled)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide">Signals</dt>
                    <dd className="text-foreground font-medium">{boolLabel(cfg.signal_enabled)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide">Live execution</dt>
                    <dd className="text-foreground font-medium">{boolLabel(cfg.live_execution_enabled)}</dd>
                  </div>
                  {vertical === 'futures' ? (
                    <div>
                      <dt className="text-xs uppercase tracking-wide">Futures engine</dt>
                      <dd className="text-foreground font-medium">{boolLabel(cfg.engine_enabled)}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt className="text-xs uppercase tracking-wide">Max daily loss (goal)</dt>
                    <dd className="text-foreground font-mono text-xs">
                      {goals.max_daily_loss_usd != null ? String(goals.max_daily_loss_usd) : '—'}
                    </dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">PnL snapshot (UTC today)</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                {pnlV ? (
                  <dl className="grid grid-cols-2 gap-2">
                    <div>
                      <dt className="text-xs text-muted-foreground">Realized today</dt>
                      <dd className="font-mono font-semibold">{Number(pnlV.realized_today ?? 0).toFixed(2)} USD</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Unrealized</dt>
                      <dd className="font-mono font-semibold">{Number(pnlV.unrealized ?? 0).toFixed(2)} USD</dd>
                    </div>
                  </dl>
                ) : (
                  <p className="text-muted-foreground">No PnL payload (controller may be offline on this replica).</p>
                )}
                {pnlPayload?.replica_has_controller === false ? (
                  <p className="text-xs text-amber-600">API reports no controller on this replica — figures may be empty.</p>
                ) : null}
              </CardContent>
            </Card>
          </div>

          <VerticalControlsPanel verticalId={vertical} onSaved={() => void load()} />
        </>
      )}
    </div>
  );
}
